import { useState, useEffect, useCallback, useMemo } from 'react';
import { Resource, CalendarMonthData } from '../../services/types';
import { CompanyOption, getCalendarMonth, createTicketInAutoTask, addTimeEntry } from '../../services/api';

interface MaintenanceViewProps {
  currentDate: Date;
  resources: Resource[];
  companies: CompanyOption[];
  userEmail: string;
}

interface ClientRow {
  companyId: number;
  companyName: string;
  hours: number;
  enabled: boolean;
}

interface SubmitResult {
  companyName: string;
  success: boolean;
  ticketNumber?: string;
  error?: string;
}

export default function MaintenanceView({ currentDate, resources, companies, userEmail }: MaintenanceViewProps) {
  const [targetDate, setTargetDate] = useState('');
  const [sharedNotes, setSharedNotes] = useState('');
  const [clientRows, setClientRows] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [results, setResults] = useState<SubmitResult[]>([]);
  const [addClientId, setAddClientId] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  const resource = useMemo(
    () => resources.find(r => r.email?.toLowerCase() === userEmail.toLowerCase()),
    [resources, userEmail]
  );

  // Default date to last Saturday of current month
  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    // Find last Saturday: start from last day of month, walk back
    const lastDay = new Date(year, month + 1, 0);
    const day = lastDay.getDay(); // 0=Sun, 6=Sat
    const diff = day >= 6 ? day - 6 : day + 1; // days to subtract to get to Saturday
    const lastSat = new Date(lastDay);
    lastSat.setDate(lastDay.getDate() - diff);
    setTargetDate(lastSat.toISOString().split('T')[0]);
  }, [currentDate]);

  // Pre-fill from previous month's maintenance entries
  useEffect(() => {
    if (!resource) return;

    const loadPreviousMonth = async () => {
      setLoading(true);
      try {
        // Previous month
        let prevMonth = currentDate.getMonth(); // 0-indexed, so this is prev month in 1-indexed
        let prevYear = currentDate.getFullYear();
        if (prevMonth === 0) {
          prevMonth = 12;
          prevYear -= 1;
        }

        const monthData: CalendarMonthData = await getCalendarMonth(prevYear, prevMonth, resource.autoTaskId);

        // Filter to maintenance entries
        const maintenanceEntries = monthData.entries.filter(e =>
          e.ticketTitle?.includes('Monthly Security Maintenance')
        );

        // Group by company, sum hours
        const companyMap = new Map<number, { companyName: string; hours: number }>();
        for (const entry of maintenanceEntries) {
          const existing = companyMap.get(entry.companyId);
          if (existing) {
            existing.hours += entry.hoursWorked;
          } else {
            companyMap.set(entry.companyId, {
              companyName: entry.companyName,
              hours: entry.hoursWorked,
            });
          }
        }

        // Convert to rows, sorted alphabetically
        const rows: ClientRow[] = Array.from(companyMap.entries())
          .map(([companyId, data]) => ({
            companyId,
            companyName: data.companyName,
            hours: Math.round(data.hours * 4) / 4, // round to nearest 0.25
            enabled: data.hours > 0,
          }))
          .sort((a, b) => a.companyName.localeCompare(b.companyName));

        setClientRows(rows);

        // Also grab a note from last month if available
        if (maintenanceEntries.length > 0 && maintenanceEntries[0].summaryNotes) {
          setSharedNotes(maintenanceEntries[0].summaryNotes);
        }

        // Check current month for duplicates
        const curMonth = currentDate.getMonth() + 1;
        const curYear = currentDate.getFullYear();
        const curData: CalendarMonthData = await getCalendarMonth(curYear, curMonth, resource.autoTaskId);
        const existing = curData.entries.filter(e =>
          e.ticketTitle?.includes('Monthly Security Maintenance')
        );
        if (existing.length > 0) {
          setDuplicateWarning(true);
        }
      } catch (err) {
        console.error('Failed to load previous month data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPreviousMonth();
  }, [resource, currentDate]);

  const enabledRows = useMemo(() => clientRows.filter(r => r.enabled && r.hours > 0), [clientRows]);
  const totalHours = useMemo(() => enabledRows.reduce((sum, r) => sum + r.hours, 0), [enabledRows]);

  const updateRow = useCallback((companyId: number, updates: Partial<ClientRow>) => {
    setClientRows(prev => prev.map(r => r.companyId === companyId ? { ...r, ...updates } : r));
  }, []);

  const removeRow = useCallback((companyId: number) => {
    setClientRows(prev => prev.filter(r => r.companyId !== companyId));
  }, []);

  const addClient = useCallback(() => {
    if (!addClientId) return;
    const company = companies.find(c => c.autoTaskId === parseInt(addClientId, 10));
    if (!company) return;
    setClientRows(prev => [
      ...prev,
      { companyId: company.autoTaskId, companyName: company.companyName, hours: 1, enabled: true },
    ].sort((a, b) => a.companyName.localeCompare(b.companyName)));
    setAddClientId('');
  }, [addClientId, companies]);

  const availableCompanies = useMemo(() => {
    const existingIds = new Set(clientRows.map(r => r.companyId));
    return companies
      .filter(c => !existingIds.has(c.autoTaskId) && c.isActive && c.source !== 'manual')
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [companies, clientRows]);

  const handleCreateAll = async () => {
    if (!resource || !targetDate || !sharedNotes.trim()) return;

    const rows = enabledRows;
    setSubmitting(true);
    setProgress({ completed: 0, total: rows.length });
    setResults([]);

    const date = new Date(targetDate + 'T12:00:00');
    const monthName = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const ticketTitle = `Monthly Security Maintenance - ${monthName} ${year}`;

    const allResults: SubmitResult[] = [];

    for (const row of rows) {
      try {
        // Step 1: Create ticket (Complete + Low priority)
        const ticket = await createTicketInAutoTask({
          companyID: row.companyId,
          title: ticketTitle,
          description: sharedNotes,
          priority: 4, // Low
          status: 5,   // Complete
        });

        // Step 2: Add time entry
        await addTimeEntry(ticket.id, {
          resourceID: resource.autoTaskId,
          hoursWorked: row.hours,
          summaryNotes: sharedNotes,
          dateWorked: targetDate,
          companyId: row.companyId,
          companyName: row.companyName,
          ticketNumber: ticket.ticketNumber,
          ticketTitle: ticketTitle,
        });

        allResults.push({ companyName: row.companyName, success: true, ticketNumber: ticket.ticketNumber });
      } catch (err) {
        allResults.push({
          companyName: row.companyName,
          success: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }

      setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
      setResults([...allResults]);
    }

    setSubmitting(false);
    setDuplicateWarning(true); // Prevent accidental re-run
  };

  const toggleAll = useCallback((enabled: boolean) => {
    setClientRows(prev => prev.map(r => ({ ...r, enabled })));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading previous month data...
      </div>
    );
  }

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  return (
    <div className="space-y-4 pb-8">
      {/* Header / Shared Fields */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Monthly Security Maintenance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Date for all entries</label>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Notes (shared across all entries)</label>
            <textarea
              value={sharedNotes}
              onChange={e => setSharedNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>
      </div>

      {/* Duplicate Warning */}
      {duplicateWarning && results.length === 0 && (
        <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-3 text-amber-300 text-sm">
          Maintenance entries already exist for this month. Submitting again will create duplicates.
        </div>
      )}

      {/* Client Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        {/* Add Client - at top for visibility */}
        <div className="px-3 py-2 border-b border-slate-700 flex items-center gap-2">
          <select
            value={addClientId}
            onChange={e => setAddClientId(e.target.value)}
            disabled={submitting}
            className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Add a client...</option>
            {availableCompanies.map(c => (
              <option key={c.autoTaskId} value={c.autoTaskId}>{c.companyName}</option>
            ))}
          </select>
          <button
            onClick={addClient}
            disabled={submitting || !addClientId}
            className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 disabled:opacity-40 text-slate-200 text-sm rounded transition-colors"
          >
            Add
          </button>
        </div>

        <div className="max-h-[45vh] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-slate-800 z-10">
            <tr className="border-b border-slate-700 text-left">
              <th className="px-3 py-2 w-10">
                <input
                  type="checkbox"
                  checked={clientRows.length > 0 && clientRows.every(r => r.enabled)}
                  onChange={e => toggleAll(e.target.checked)}
                  disabled={submitting}
                  className="rounded border-slate-500"
                />
              </th>
              <th className="px-3 py-2 text-sm font-medium text-slate-300">Company</th>
              <th className="px-3 py-2 text-sm font-medium text-slate-300 w-24 text-right">Hours</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {clientRows.map(row => (
              <tr
                key={row.companyId}
                className={`border-b border-slate-700/50 ${!row.enabled ? 'opacity-40' : ''}`}
              >
                <td className="px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={e => updateRow(row.companyId, { enabled: e.target.checked })}
                    disabled={submitting}
                    className="rounded border-slate-500"
                  />
                </td>
                <td className="px-3 py-1.5 text-sm text-slate-200">{row.companyName}</td>
                <td className="px-3 py-1.5 text-right">
                  <input
                    type="number"
                    min="0"
                    max="24"
                    step="0.25"
                    value={row.hours}
                    onChange={e => updateRow(row.companyId, { hours: parseFloat(e.target.value) || 0 })}
                    disabled={submitting}
                    className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-200 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </td>
                <td className="px-3 py-1.5 text-center">
                  <button
                    onClick={() => removeRow(row.companyId)}
                    disabled={submitting}
                    className="text-slate-500 hover:text-red-400 transition-colors"
                    title="Remove"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Summary + Submit */}
      <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 py-3 px-1 flex items-center justify-between z-20">
        <span className="text-sm text-slate-400">
          {enabledRows.length} client{enabledRows.length !== 1 ? 's' : ''} &middot; {totalHours} total hours
        </span>
        <button
          onClick={handleCreateAll}
          disabled={submitting || enabledRows.length === 0 || !targetDate || !sharedNotes.trim()}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          {submitting
            ? `Creating... (${progress.completed}/${progress.total})`
            : 'Create All'
          }
        </button>
      </div>

      {/* Progress bar during submission */}
      {submitting && (
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
          />
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
          <h3 className="text-sm font-medium text-slate-300 mb-3">
            Results: {successCount} created{failCount > 0 ? `, ${failCount} failed` : ''}
          </h3>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {results.map((r, i) => (
              <div key={i} className={`text-sm ${r.success ? 'text-green-400' : 'text-red-400'}`}>
                {r.success ? '\u2713' : '\u2717'} {r.companyName}
                {r.ticketNumber && <span className="text-slate-500 ml-2">{r.ticketNumber}</span>}
                {r.error && <span className="text-red-500 ml-2">— {r.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
